import {
  useFetchInvitationRecords,
  useFetchMultipleInvitationRecords,
} from "@core/features/invitation";
import { Loader } from "@themes/pollinator/ui/Loader";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { InvitationRecordsTable } from "./InvitationRecordsTable";

type SingleInvitationProps = {
  invitationId: number;
  invitationIds?: never;
};

type MultipleInvitationProps = {
  invitationId?: never;
  invitationIds: number[];
};

export type Props = SingleInvitationProps | MultipleInvitationProps;

/**
 * Container that fetches and displays invitation records.
 * Supports both single invitation (invitationId) and multiple invitations (invitationIds).
 */
export function InvitationRecordsTableContainer(props: Props) {
  const isMultiple = "invitationIds" in props && props.invitationIds;

  // Single invitation fetch
  const singleQuery = useFetchInvitationRecords({
    invitationId: isMultiple ? 0 : props.invitationId,
    enabled: !isMultiple && !!props.invitationId,
  });

  // Multiple invitations fetch
  const multiQuery = useFetchMultipleInvitationRecords({
    invitationIds: isMultiple ? props.invitationIds : [],
    enabled: isMultiple && props.invitationIds.length > 0,
  });

  const isLoading = isMultiple ? multiQuery.isLoading : singleQuery.isLoading;
  const records = isMultiple ? multiQuery.data : singleQuery.data ?? [];
  const showInvitationColumn = isMultiple && props.invitationIds.length > 1;

  const title = isMultiple
    ? "All Invitation Records"
    : "Sent Invitations";
  const description = isMultiple
    ? "Track the status of all participant invitations across programs."
    : "Track the status of invitations you've sent.";

  return (
    <SectionHeading title={title} description={description} className="gap-6">
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <InvitationRecordsTable
          records={records}
          showInvitationColumn={showInvitationColumn}
        />
      )}
    </SectionHeading>
  );
}

export default InvitationRecordsTableContainer;
